import { Aide } from '@/components/ui/aide';
import { useSyncOuEnLigne } from '../../context/SyncContext';
import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';

import { inboundRentalsAPI, decalerMois, moisPrecedent } from '../../lib/inboundRentalsApi';
import { messageErreur } from '../../lib/erreurs';
import {
  formatDate,
  formatDateLongue,
  formatMois,
  formatNombre,
  francisaTexte,
} from '../../lib/formats';
import { libelle, nomLisible, texteSur, ton } from '../../lib/libelles';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { EmptyState } from '../ui/empty-state';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { LoadingState } from '../ui/loading-state';
import { StatsCompactes } from '../ui/stats-compactes';
import { toast } from '../ui/sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';

const nombre = (valeur) => formatNombre(valeur, 0, '—');

/** Un chiffre du tableau: aligné à droite, rouge s'il est négatif. */
const Chiffre = ({ valeur, signe = false, className }) => (
  <TableCell
    className={cn(
      'whitespace-nowrap text-right tabular-nums',
      signe && Number(valeur) < 0 && 'text-destructive',
      className,
    )}
  >
    {nombre(valeur)}
  </TableCell>
);

/**
 * Le compte d'exploitation d'un camion pour le mois, comme dans le classeur
 * Excel de l'exploitant: Date | Tracteur | Citerne | Rotation | Recettes |
 * Charges | Résultat | Observations, et sa ligne de totaux.
 */
export const ReleveCamion = ({ camion }) => (
  <div className="space-y-1.5" data-testid={`releve-camion-${camion.line_id}`}>
    <h3 className="text-sm font-bold text-foreground">
      {nomLisible(camion.tractor_plate, 'Camion')}
      {camion.tank_plate ? <span className="font-normal text-muted-foreground"> · citerne {camion.tank_plate}</span> : null}
      <span className="ml-2 font-normal text-muted-foreground">
        {camion.rotations} rotation{camion.rotations > 1 ? 's' : ''}
      </span>
    </h3>
    <div className="overflow-x-auto rounded-lg border border-border/70">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Tracteur</TableHead>
            <TableHead>Citerne</TableHead>
            <TableHead>Rotation</TableHead>
            <TableHead className="text-right">Recettes</TableHead>
            <TableHead className="text-right">Charges</TableHead>
            <TableHead className="text-right">Résultat</TableHead>
            <TableHead>Observations</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {camion.rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="py-4 text-center text-sm text-muted-foreground">
                Aucune rotation ce mois-ci
              </TableCell>
            </TableRow>
          ) : (
            camion.rows.map((ligne, index) => (
              <TableRow key={`${ligne.date}-${ligne.trip_id || 'frais'}-${index}`}>
                <TableCell className="whitespace-nowrap tabular-nums">{formatDate(ligne.date)}</TableCell>
                <TableCell>{nomLisible(ligne.tractor_plate, '—')}</TableCell>
                <TableCell>{nomLisible(ligne.tank_plate, '—')}</TableCell>
                <TableCell>
                  {ligne.kind === 'CHARGE' ? libelle('ligne_releve', 'CHARGE') : texteSur(ligne.rotation, libelle('ligne_releve', 'ROTATION'))}
                </TableCell>
                <Chiffre valeur={ligne.revenue} />
                <Chiffre valeur={ligne.charges} />
                <Chiffre valeur={ligne.result} signe />
                <TableCell className="max-w-[18rem] text-sm text-muted-foreground">
                  {(ligne.notes || []).map((note) => francisaTexte(note)).join(' ; ')}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
        <TableFooter>
          <TableRow className="font-bold">
            <TableCell colSpan={4}>Total</TableCell>
            <Chiffre valeur={camion.revenue} />
            <Chiffre valeur={camion.charges} />
            <Chiffre valeur={camion.result} signe />
            <TableCell />
          </TableRow>
        </TableFooter>
      </Table>
    </div>
    {(camion.warnings || []).map((avertissement) => (
      <p key={avertissement} className="text-xs text-flame-ink">
        {francisaTexte(avertissement)}
      </p>
    ))}
  </div>
);

const InboundStatementPanel = ({ contract, onChanged }) => {
  const [month, setMonth] = useState(() => moisPrecedent());
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState('');
  // L'argent attend le serveur: boutons inactifs sans lui.
  const enLigne = useSyncOuEnLigne().isOnline !== false;
  const [confirmer, setConfirmer] = useState(false);

  const charger = useCallback(async ({ silencieux = false } = {}) => {
    if (!/^\d{4}-\d{2}$/.test(month)) return;
    if (!silencieux) setLoading(true);
    setFailed(false);
    try {
      setStatement(await inboundRentalsAPI.statement(contract.id, month));
    } catch (error) {
      setStatement(null);
      setFailed(true);
      toast.error(messageErreur(error, 'Impossible de calculer le relevé de ce mois'));
    } finally {
      setLoading(false);
    }
  }, [contract.id, month]);

  useEffect(() => {
    charger();
  }, [charger]);

  useRafraichirSur(['locations', 'depenses', 'trajets', 'finance'], charger);

  const valide = statement?.status === 'VALIDE';
  const brouillonContrat = contract.status === 'BROUILLON';

  const enregistrerBrouillon = async () => {
    setBusy('brouillon');
    try {
      await inboundRentalsAPI.saveSettlementDraft(contract.id, month);
      toast.success('Relevé enregistré en brouillon');
      await Promise.all([charger(), onChanged()]);
    } catch (error) {
      toast.error(messageErreur(error, 'Enregistrement du brouillon impossible'));
    } finally {
      setBusy('');
    }
  };

  const valider = async () => {
    setBusy('valider');
    try {
      await inboundRentalsAPI.validateSettlement(contract.id, month);
      toast.success(`Relevé de ${formatMois(month)} validé`);
      setConfirmer(false);
      await Promise.all([charger(), onChanged()]);
    } catch (error) {
      setConfirmer(false);
      toast.error(messageErreur(error, 'Validation du relevé impossible'));
    } finally {
      setBusy('');
    }
  };

  const exporter = async (format) => {
    setBusy(format);
    try {
      await inboundRentalsAPI.downloadStatement(contract.id, month, format);
    } catch (error) {
      toast.error(messageErreur(error, 'Téléchargement du relevé impossible'));
    } finally {
      setBusy('');
    }
  };

  const totaux = statement?.totals;
  const fraisProprietaire = totaux
    ? Number(totaux.charges_deducted || 0) + Number(totaux.charges_already_in_account || 0)
    : null;
  const net = totaux?.net_owner;
  const aPayer = totaux?.bill_amount;

  return (
    <div className="space-y-5" data-testid="inbound-statement-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="releve-mois">Mois du relevé</Label>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              aria-label="Mois précédent"
              onClick={() => setMonth((m) => decalerMois(m, -1))}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Input
              id="releve-mois"
              type="month"
              value={month}
              onChange={(event) => event.target.value && setMonth(event.target.value)}
              className="w-[11rem]"
              data-testid="releve-mois"
            />
            <Button
              variant="outline"
              size="icon"
              aria-label="Mois suivant"
              onClick={() => setMonth((m) => decalerMois(m, 1))}
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>

        {(contract.settlements || []).length > 0 ? (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Relevés déjà établis</p>
            <div className="flex flex-wrap gap-1.5">
              {contract.settlements.map((reglement) => (
                <button
                  key={reglement.id}
                  type="button"
                  onClick={() => setMonth(reglement.month)}
                  aria-pressed={reglement.month === month}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                    reglement.month === month ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted',
                  )}
                >
                  {formatMois(reglement.month)} · {libelle('statut_reglement', reglement.status)}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!statement || Boolean(busy)}
            onClick={() => exporter('pdf')}
            data-testid="export-releve-pdf"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Relevé en PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!statement || Boolean(busy)}
            onClick={() => exporter('xlsx')}
            data-testid="export-releve-xlsx"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Relevé en Excel
          </Button>
          {!valide ? (
            <Button
              variant="outline"
              size="sm"
              disabled={!enLigne || !statement || Boolean(busy) || brouillonContrat}
              onClick={enregistrerBrouillon}
              data-testid="save-releve-draft"
            >
              Enregistrer en brouillon
            </Button>
          ) : null}
          <Button
            size="sm"
            disabled={!enLigne || !statement || valide || Boolean(busy) || brouillonContrat}
            title={brouillonContrat ? 'Activez d’abord le contrat' : undefined}
            onClick={() => setConfirmer(true)}
            data-testid="validate-releve-btn"
          >
            Valider le relevé
          </Button>
        </div>
      </div>

      {brouillonContrat ? (
        <p className="flex items-center gap-1 rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
          Ce contrat est encore en brouillon.
          <Aide titre="Contrat en brouillon">
            L’aperçu est possible, la validation du relevé demande un contrat activé.
          </Aide>
        </p>
      ) : null}

      {loading && !statement ? (
        <LoadingState shape="table" />
      ) : failed || !statement ? (
        <EmptyState
          variant="error"
          title="Le relevé n’a pas pu être calculé"
          description="Choisissez un autre mois ou réessayez dans un instant."
          actionLabel="Réessayer"
          onAction={charger}
        />
      ) : (
        <div className={cn('space-y-5', loading && 'opacity-60')}>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-base font-bold capitalize text-foreground">Relevé de {formatMois(month)}</h2>
            <Badge variant={ton('statut_reglement', statement.status)} data-testid="releve-statut">
              {libelle('statut_reglement', statement.status)}
            </Badge>
            <p className="text-sm text-muted-foreground">
              {valide
                ? 'Relevé validé : le fret du mois est enregistré au compte du propriétaire.'
                : 'Relevé en attente de validation : le règlement du mois n’est pas encore validé.'}
            </p>
          </div>

          {(statement.warnings || []).length > 0 ? (
            <ul className="space-y-1 rounded-lg border border-flame/30 bg-flame/10 p-3 text-sm text-flame-ink" data-testid="releve-avertissements">
              {statement.warnings.map((avertissement) => (
                <li key={avertissement}>{francisaTexte(avertissement)}</li>
              ))}
            </ul>
          ) : null}

          <StatsCompactes
            stats={[
              { label: 'Recettes', valeur: totaux.freight, unite: 'FCFA' },
              { label: 'Fret dû', valeur: totaux.rent_due, unite: 'FCFA' },
              { label: 'Frais du propriétaire', valeur: fraisProprietaire, unite: 'FCFA' },
              { label: 'Net à payer', valeur: net, unite: 'FCFA', ton: 'positif', testid: 'releve-net' },
            ]}
            plus={
              <ul className="space-y-1 text-sm">
                <li className="flex justify-between gap-3">
                  <span>Rotations</span>
                  <span className="tabular-nums">{Number(totaux.rotations ?? 0).toLocaleString('fr-FR')}</span>
                </li>
                <li className="flex justify-between gap-3">
                  <span>Commission</span>
                  <span className="tabular-nums">{Number(totaux.commission ?? 0).toLocaleString('fr-FR')} FCFA</span>
                </li>
              </ul>
            }
          />

          {statement.trucks.length === 0 ? (
            <EmptyState
              illustration="donnee"
              title="Aucun camion ce mois-ci"
              description="Aucun camion de ce contrat ne couvre ce mois. Vérifiez les dates des camions et du contrat."
            />
          ) : (
            statement.trucks.map((camion) => <ReleveCamion key={camion.line_id} camion={camion} />)
          )}

          <div className="space-y-1.5">
            <h3 className="text-sm font-bold text-foreground">Règlement du mois</h3>
            <div className="overflow-x-auto rounded-lg border border-border/70">
              <Table data-testid="reglement-du-mois">
                <TableHeader>
                  <TableRow>
                    <TableHead>Camion</TableHead>
                    <TableHead className="text-right">Rotations</TableHead>
                    <TableHead className="text-right">Recettes</TableHead>
                    <TableHead className="text-right">Fret dû</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead className="text-right">Frais du propriétaire</TableHead>
                    <TableHead className="text-right">Net à payer</TableHead>
                    <TableHead>Écriture au compte</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statement.trucks.map((camion) => (
                    <TableRow key={camion.line_id}>
                      <TableCell className="font-semibold">{nomLisible(camion.tractor_plate, 'Camion')}</TableCell>
                      <Chiffre valeur={camion.rotations} />
                      <Chiffre valeur={camion.revenue} />
                      <Chiffre valeur={camion.rent_due} />
                      <Chiffre valeur={camion.commission} />
                      <Chiffre valeur={Number(camion.charges_deducted || 0) + Number(camion.charges_already_in_account || 0)} />
                      <Chiffre valeur={camion.net_owner} signe className="font-semibold" />
                      <TableCell className="text-sm text-muted-foreground">
                        {camion.bill_kind ? libelle('nature_ecriture_reglement', camion.bill_kind) : 'Aucune'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow className="font-bold">
                    <TableCell>Total</TableCell>
                    <Chiffre valeur={totaux.rotations} />
                    <Chiffre valeur={totaux.freight} />
                    <Chiffre valeur={totaux.rent_due} />
                    <Chiffre valeur={totaux.commission} />
                    <Chiffre valeur={fraisProprietaire} />
                    <Chiffre valeur={net} signe />
                    <TableCell />
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3">
              <p className="text-sm font-semibold">
                {Number(aPayer) < 0 ? 'Le propriétaire doit à la société' : 'Net à payer au propriétaire'}
              </p>
              <p className="kpi-value tabular-nums" data-testid="releve-net-total">
                {nombre(Math.abs(Number(aPayer ?? net ?? 0)))} <span className="text-sm font-medium text-muted-foreground">FCFA</span>
              </p>
              <p className="w-full text-xs text-muted-foreground">
                Marge de la société sur ce mois : {totaux.company_margin === null || totaux.company_margin === undefined ? 'fret non renseigné' : `${nombre(totaux.company_margin)} FCFA`}
                {' · '}
                Solde du compte du propriétaire : {nombre(statement.account?.balance)} FCFA
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <h3 className="text-sm font-bold text-foreground">Avances et paiements du mois</h3>
            {(statement.advances || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucune avance ce mois-ci.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border/70">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Montant</TableHead>
                      <TableHead>Observations</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {statement.advances.map((avance) => (
                      <TableRow key={avance.id}>
                        <TableCell className="whitespace-nowrap tabular-nums">{formatDate(avance.date)}</TableCell>
                        <Chiffre valeur={avance.amount} />
                        <TableCell className="text-sm text-muted-foreground" data-testid={`releve-avance-${avance.id}`}>
                          {[
                            texteSur(avance.note, ''),
                            (avance.truck_plates || []).length > 0 ? `Camions : ${avance.truck_plates.join(', ')}` : '',
                          ]
                            .filter(Boolean)
                            .join(' — ')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <p className="text-sm text-muted-foreground" data-testid="releve-signature">
            Responsable Transport — Fait à {statement.city || '……………'}, le {formatDateLongue(new Date())}
          </p>
        </div>
      )}

      <AlertDialog open={confirmer} onOpenChange={setConfirmer}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Valider le relevé de {formatMois(month)} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Une facture fournisseur est enregistrée au compte du propriétaire pour chaque camion. Le mois est ensuite
              figé : il ne pourra plus être recalculé. Valider deux fois le même mois ne crée rien de plus.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Pas maintenant</AlertDialogCancel>
            <AlertDialogAction onClick={valider} disabled={busy === 'valider' || !enLigne} data-testid="confirm-validate-releve">
              Valider le relevé
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InboundStatementPanel;
